import { useRef, useState, useEffect, Suspense, lazy } from "react";
import { motion, useScroll, useTransform, useMotionValueEvent, useInView } from "framer-motion";
import { MATES } from "../lib/data";

const MatesScene = lazy(() => import("./MatesScene"));

function useWebGL() {
  const [ok, setOk] = useState(false);
  useEffect(() => {
    try {
      const c = document.createElement("canvas");
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      setOk(!reduced && !!(window.WebGLRenderingContext && c.getContext("webgl")));
    } catch {
      setOk(false);
    }
  }, []);
  return ok;
}

export default function Mates() {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-10% 0px" });
  const webgl = useWebGL();
  const [active, setActive] = useState(0);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end end"],
  });

  useMotionValueEvent(scrollYProgress, "change", (p) => {
    // 5 pillars across the scroll range
    const idx = Math.min(MATES.length - 1, Math.floor(p * MATES.length));
    setActive(idx < 0 ? 0 : idx);
  });

  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);

  return (
    <section
      id="why-us"
      data-testid="mates-section"
      ref={ref}
      className="relative bg-[var(--bg-primary)]"
      style={{ height: "480vh" }}
    >
      <div className="sticky top-0 h-screen w-full overflow-hidden flex flex-col justify-center px-6 md:px-12 lg:px-16">
        {/* parallax glow */}
        <motion.div
          style={{ y: bgY }}
          className="absolute left-1/2 top-1/2 -z-0 h-[60vw] w-[60vw] max-w-[700px] max-h-[700px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[radial-gradient(circle,rgba(124,52,235,0.25),transparent_60%)] blur-3xl"
        />

        {/* 3D pillar visual — morphs per active MATES letter */}
        {webgl && inView && (
          <div className="absolute right-0 top-0 h-full w-full lg:w-[46%] opacity-40 lg:opacity-100 pointer-events-none">
            <Suspense fallback={null}>
              <MatesScene index={active} />
            </Suspense>
          </div>
        )}
        <div className="absolute inset-0 pointer-events-none bg-gradient-to-r from-[var(--bg-primary)] via-[var(--bg-primary)]/80 lg:via-[var(--bg-primary)]/40 to-transparent" />

        <div className="relative z-10">
          <p className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-[var(--accent-primary)]">
            02 — Why Us
          </p>
          <h2 className="mt-4 font-display text-3xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white max-w-3xl">
            Because every brand deserves a true MATE!
          </h2>

          {/* Letter bar */}
          <div className="mt-10 md:mt-14 flex items-center gap-2 md:gap-4" data-testid="mates-letterbar">
            {MATES.map((m, i) => (
              <span
                key={m.letter}
                className={`font-display font-black leading-none transition-all duration-500 text-6xl md:text-8xl lg:text-[9rem] ${
                  i === active
                    ? "text-[var(--accent-secondary)] scale-100"
                    : i < active
                    ? "text-white scale-90"
                    : "text-transparent [-webkit-text-stroke:1.5px_rgba(255,255,255,0.2)] scale-90"
                }`}
              >
                {m.letter}
              </span>
            ))}
          </div>

          {/* Active pillar detail */}
          <div className="mt-10 md:mt-14 grid md:grid-cols-12 gap-6 items-start max-w-5xl">
            <div className="md:col-span-4">
              <motion.div
                key={`t-${active}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <div className="font-display text-2xl md:text-3xl font-bold text-white">
                  {MATES[active].letter} — {MATES[active].title}
                </div>
                <div className="mt-2 text-sm md:text-base font-medium text-[var(--accent-primary-hover)]">
                  {MATES[active].sub}
                </div>
              </motion.div>
            </div>
            <motion.p
              key={`d-${active}`}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.05 }}
              className="md:col-span-8 text-base md:text-xl text-zinc-300 leading-relaxed"
              data-testid="mates-active-desc"
            >
              {MATES[active].desc}
            </motion.p>
          </div>

          {/* progress */}
          <div className="mt-10 h-px w-full max-w-5xl bg-white/10 relative overflow-hidden">
            <motion.div
              style={{ scaleX: scrollYProgress }}
              className="absolute inset-0 origin-left bg-[var(--accent-secondary)]"
            />
          </div>
        </div>
      </div>
    </section>
  );
}
